"use client";

import React, { useEffect, useState } from "react";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { toWebp } from "@/utils/common";
import Link from "next/link";

interface Props {
  data: any;
}

const RequestProposalInsight: React.FC<Props> = ({ data }) => {
  const [loaded, setLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const url = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL;

  const [plugins, setPlugins] = useState<any[]>([]);

  // 👇 Run only on client after hydration
  useEffect(() => {
    if (window.innerWidth < 768) {
      setPlugins([autoplay]);
    }
  }, []);

  // Autoplay plugin
  const autoplay = (slider: any) => {
    let timeout: NodeJS.Timeout;
    let mouseOver = false;

    const clearNextTimeout = () => {
      clearTimeout(timeout);
    }

    const nextTimeout = () => {
      clearTimeout(timeout);
      if (mouseOver) return;
      timeout = setTimeout(() => {
        slider.next();
      }, 3000); // autoplay interval
    }

    slider.on("created", () => {
      slider.container.addEventListener("mouseover", () => {
        mouseOver = true;
        clearNextTimeout();
      });
      slider.container.addEventListener("mouseout", () => {
        mouseOver = false;
        nextTimeout();
      });
      nextTimeout();
    });

    slider.on("dragStarted", clearNextTimeout);
    slider.on("animationEnded", nextTimeout);
    slider.on("updated", nextTimeout);
  };

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      loop: true,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
      slides: {
        perView: 1,
        spacing: 0,
      },
      breakpoints: {
        "(min-width: 768px)": {
          slides: { perView: 2, spacing: 0 },
        },
        "(min-width: 1280px)": {
          slides: { perView: 3, spacing: 0 },
        },
      },
    },
    // Enable autoplay ONLY for mobile
    plugins,
  );

  return (
    <>
      <section className="relative z-[0] overflow-hidden py-[56px] lg:py-[80px] xl:py-[100px] bg-white before:content-[''] before:absolute before:h-[100%] before:inset-0 before:bg-[url('/images/client-bg.png')] before:bg-no-repeat before:bg-cover before:bg-center before:bg-[#F0F8FF] before:-z-10">
        <div className="container">
          <div className="heading mb-[40px] lg:mb-[40px] xl:mb-[40px]">
            <h2 className="font-bold text-primary-text text-heading-1 text-center">
              {data?.title ?? ""}
            </h2>
          </div>

          {/* Desktop Grid */}
          <div className="md:grid grid-cols-1 md:grid-cols-3 xl:grid-cols-3 gap-[16px] xl:gap-[30px] hidden">
            {data?.Blog?.map((item: any, i: number) => (
              <div key={"Blog" + i} className="caseCard h-full relative">
                {item?.blog_link?.href && (
                  <Link href={item?.blog_link?.href}>
                    <div
                      className="aspect-[390_/_593] w-full h-full xl:min-h-[593px] md:min-h-[480px] min-h-[593px] max-h-[593px] flex flex-col justify-between overflow-hidden"
                      style={{
                        backgroundSize: "100% 100%",
                        backgroundImage: `url(${toWebp(url + item?.blog_image?.data?.attributes?.url)})`,
                      }}
                    >
                      <div
                        className={`bg-[${(i + 1) % 2 !== 0 ? "#B0D9F7" : "#DBEAB0"}] p-[4px] absolute top-[0px] left-0 w-full`}
                      >
                        <p className="text-center text-[#003861] text-[16px] font-regular !leading-[22px] mx-auto">
                          Read Time : {item.read_time}
                        </p>
                      </div>
                      <div className="bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0.08)_0%,_rgba(0,_0,_0,_0.8)_60.02%)] py-6 px-4 grow flex flex-col justify-end">
                        <div>
                          <div>
                            <h3 className="xl:text-[32px]  xl:leading-[41.2px]  lg:text-[24px] lg:leading-[31.2px] text-[22px] leading-[28px] font-[700] text-white">
                              {item.Title}
                            </h3>
                            <div className="bg-[#040405] flex justify-between p-[8px_4px] mt-[10px] flex-wrap">
                              <p className="text-[#B0D9F7] xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] font-[600]">
                                Posted On :
                              </p>
                              <p className="text-[#B0D9F7] xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] font-[600]">
                                {item.posted_on}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Slider */}
          <div className="md:hidden block">
            <div ref={sliderRef} className="keen-slider">
              {data?.Blog?.map((item: any, i: number) => (
                <div
                  key={"Blog" + i}
                  className="keen-slider__slide caseCard h-full"
                >
                  <div
                    className="aspect-[390_/_593] w-full h-full flex flex-col justify-between"
                    style={{
                      backgroundImage: `url(${toWebp(url + item?.blog_image?.data?.attributes?.url)})`,
                      backgroundSize: "cover",
                    }}
                  >
                    <div
                      className={`${(i + 1) % 2 !== 0 ? "bg-border" : "bg-[#DBEAB0]"}`}
                    >
                      <p className="text-center text-blue-900 pt-1 text-[16px] font-regular">
                        Read Time : {item.read_time}
                      </p>
                    </div>
                    <div className="bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0.08)_0%,_rgba(0,_0,_0,_0.8)_60.02%)] py-6 px-4 grow flex flex-col justify-end">
                      <h3 className="text-[22px] font-[700] text-white">
                        {item.Title}
                      </h3>
                      <div className="bg-[#040405] flex justify-between p-[8px_4px] mt-[10px]">
                        <p className="text-[#B0D9F7] text-[16px] font-[600]">
                          Posted On :
                        </p>
                        <p className="text-[#B0D9F7] text-[16px] font-[600]">
                          {item.posted_on}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {loaded && instanceRef.current && (
              <div className="flex justify-center items-center gap-2 px-12 py-6 w-[67%] mx-auto">
                {Array.from({
                  length:
                    instanceRef?.current?.track?.details?.slides?.length || 0,
                }).map((_, idx) => (
                  <button
                    aria-label="dot"
                    key={idx}
                    onClick={() => instanceRef.current?.moveToIdx(idx)}
                    className={`p-0 rounded relative overflow-hidden ${
                      currentSlide === idx
                        ? "flex-1 min-w-[60px] w-16 h-1 bg-background"
                        : "flex-none w-4 h-1 bg-background"
                    }`}
                  >
                    <span
                      className={`absolute rounded top-0 left-0 w-full h-1 ${
                        currentSlide === idx
                          ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[2000ms] delay-[200ms]"
                          : "left-[-100%]"
                      }`}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default RequestProposalInsight;
